import styled from "styled-components";
import { useMemo } from "react";

import {
  ShortlistCardActionButtons,
  IShortlistCardProps,
} from "../ShortlistCard";
import { SalaryTechExperienceLocation } from "../../Jobs/JobCard/responsiveComponents/SalaryTechExperienceLocation";

import { Benefits } from "./SpotlightCards/Benefits";
import { CarouselCard } from "./SpotlightCards/components";
import { TeamMission } from "./SpotlightCards/TeamMission";
import { MeetTheTeam } from "./SpotlightCards/MeetTheTeam";
import { Headquarters } from "./SpotlightCards/Headquarters";

import { Card, media } from "@otta/design";
import { palette, pxToRem } from "@otta/design-tokens";
import {
  BrandAssetGroup,
  DashboardSpotlightCompanyFragment,
  Job,
  ShortlistJobDocument,
  UserJobPreferences,
  UserJobPreferencesDocument,
} from "@otta/search/schema";
import { useQuery } from "@otta/search/apollo";
import { Loading } from "@otta/shared-components";
import { useUserLocation } from "@otta/search/hooks/useUserLocation";
import { countryToCurrency } from "@otta/search/utils/currencies";
import { JobInfo } from "@otta/search/components/JobPreviewCard";
import { Carousel } from "@otta/search/components/Carousel";

const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

const SpotlightCarouselContainer = styled.div`
  align-items: center;
  background: ${palette.grayscale.shade50};
  flex-grow: 1;
  overflow: hidden;
  padding: ${pxToRem(16)};

  ${media({ from: "desktop" })`
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: ${pxToRem(52)};
    border-radius: 0;

    .carousel-card-hidden {
      .spotlight-content-tag {
        display: none;
      }
    }
  `}
`;

const StaticContainer = styled(Column)`
  gap: ${pxToRem(24)};

  ${media({ from: "tablet" })`
    justify-content: space-between;
    padding: ${pxToRem(24)};
    max-width: ${pxToRem(320)};
  `}
`;

const StaticTopSection = styled(Column)`
  ${media({ to: "tablet" })`
    background: ${palette.brand.white};
    padding: ${pxToRem(16)};
  `}
`;

const MobileContainer = styled(Column)`
  border-radius: ${pxToRem(16)};
  overflow: hidden;
`;

function StaticSection({
  job,
  section,
  onChange,
  user,
  isMobile = false,
}: Pick<IShortlistCardProps, "section" | "onChange"> & {
  user?: UserJobPreferences.CurrentUser;
  job: Job.Fragment;
  isMobile?: boolean;
}) {
  return (
    <StaticContainer>
      <StaticTopSection>
        <JobInfo
          title={job.title}
          subtitle={job.subtitle}
          company={job.company}
        />
        {isMobile ? (
          <Row style={{ gap: pxToRem(8) }}>
            <ShortlistCardActionButtons
              job={job}
              section={section}
              onChange={onChange}
            />
          </Row>
        ) : (
          <SalaryTechExperienceLocation job={job} user={user} />
        )}
      </StaticTopSection>
      {!isMobile && (
        <Row style={{ gap: pxToRem(8) }}>
          <ShortlistCardActionButtons
            job={job}
            section={section}
            onChange={onChange}
          />
        </Row>
      )}
    </StaticContainer>
  );
}

function SpotlightCarousel({
  cards,
  isMobile = false,
}: {
  cards: React.ReactElement[];
  isMobile?: boolean;
}) {
  return (
    <SpotlightCarouselContainer>
      <Carousel cards={cards} displayDots={isMobile} />
    </SpotlightCarouselContainer>
  );
}

export function SpotlightShortlistCard({
  jobId,
  company,
  section,
  onChange,
  isMobile = true,
}: Omit<IShortlistCardProps, "job"> & {
  jobId: string;
  company: DashboardSpotlightCompanyFragment;
  isMobile?: boolean;
}) {
  const { data: userData, loading: userLoading } = useQuery(
    UserJobPreferencesDocument
  );

  const { country: userLocation } = useUserLocation();
  const currency = countryToCurrency(userLocation);

  const { data, loading } = useQuery(ShortlistJobDocument, {
    variables: { externalId: jobId as string, currency },
  });

  const cards = useMemo(() => {
    const benefitLinks = company.brandAssetGroups.find(
      ({ group }) => group.name === BrandAssetGroup.CompanySpotlightBenefit
    )?.links;

    const meetTheTeamLinks = company.brandAssetGroups.find(
      ({ group }) => group.name === BrandAssetGroup.TeamCardHeader
    )?.links;

    const officeAssets = company.brandAssetGroups.filter(
      ({ group }) => group.name === BrandAssetGroup.CompanySpotlightOffice
    );

    const spotlightCards = [];

    if (meetTheTeamLinks && meetTheTeamLinks?.length > 0) {
      spotlightCards.push(<MeetTheTeam asset={meetTheTeamLinks[0]} />);
    }

    if (benefitLinks && benefitLinks?.length > 0) {
      spotlightCards.push(<Benefits asset={benefitLinks[0]} />);
    }

    if (data?.publicJob?.team?.mission) {
      spotlightCards.push(
        <TeamMission
          name={data.publicJob.team.name}
          mission={data.publicJob.team.mission}
        />
      );
    }

    if (
      company?.offices &&
      company.offices?.length > 0 &&
      officeAssets?.length > 0
    ) {
      spotlightCards.push(
        <Headquarters assets={officeAssets} office={company.offices?.at(0)} />
      );
    }

    return spotlightCards;
  }, [company.brandAssetGroups, data?.publicJob]);

  if (loading || userLoading) {
    return <Loading />;
  }

  if (!data?.publicJob) {
    return null;
  }

  return (
    <div data-testid="spotlight-preview-card">
      {isMobile ? (
        <MobileContainer>
          <StaticSection
            key="static-section-asset-card"
            job={data.publicJob}
            section={section}
            onChange={onChange}
            user={userData?.currentUser ?? undefined}
            isMobile
          />
          <SpotlightCarousel
            cards={[
              <CarouselCard>
                <SalaryTechExperienceLocation
                  job={data.publicJob}
                  user={userData?.currentUser ?? undefined}
                />
              </CarouselCard>,
              ...cards,
            ]}
            isMobile
          />
        </MobileContainer>
      ) : (
        <Card
          style={{
            position: "relative",
            overflow: "hidden",
            padding: 0,
          }}
        >
          <Row style={{ minHeight: pxToRem(392) }}>
            <StaticSection
              job={data.publicJob}
              section={section}
              onChange={onChange}
              user={userData?.currentUser ?? undefined}
            />
            <SpotlightCarousel cards={cards} />
          </Row>
        </Card>
      )}
    </div>
  );
}
